import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { ConfigContext } from '../../../contexts/config-context';
import MessageAPI from '../../../axios/message';
import { formatNumber } from '../../main-page/home-utils';
import { listingCover } from './result-card';
// the dialog is also used outside the search page, so it loads its own styles
import '../search-page.css';

const TEMPLATES = ['msg_template_1', 'msg_template_2', 'msg_template_3'];

export const MessageModal = ({ listing, onClose }) => {
    const { api } = useContext(ConfigContext);
    const { t } = useTranslation(["results", "listing"]);
    const [subject, setSubject] = useState(() => t('msg_default_subject', { title: listing.title }));
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const price = parseFloat(listing.price) || 0;
    // search results carry the publisher as an object, listing details as a plain name
    const recipient = typeof listing.user === 'string'
        ? listing.user
        : [listing.user?.first_name, listing.user?.last_name].filter(Boolean).join(' ') || listing.user?.user_type?.label;
    const meta = [
        price > 0 ? `${formatNumber(price)} €` : t('price_placeholder', { ns: 'listing' }),
        `${formatNumber(listing.area)} m²`,
        listing.subplace?.label || listing.place?.label,
    ].filter(Boolean).join(' · ');
    const canSend = body.trim().length > 0 && !sending;

    const send = () => {
        if (!canSend) return;
        setSending(true);
        setError(false);
        MessageAPI.sendMessage({ subject, body, reply_to: null, listing: listing.id })
            .then(() => setSent(true))
            .catch(() => setError(true))
            .finally(() => setSending(false));
    };

    return (
        <div className='hs-msg-overlay' onClick={onClose}>
            <div className='hs-msg' role='dialog' aria-label={t('msg_title')} onClick={(e) => e.stopPropagation()}>
                {sent ?
                    <div className='hs-msg-sent'>
                        <span className='hs-msg-sent-icon'>
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
                        </span>
                        <h2>{t('msg_sent_title')}</h2>
                        <p>{t('msg_sent_text', { name: recipient })}</p>
                        <button onClick={onClose}>{t('close')}</button>
                    </div>
                    :
                    <>
                        <div className='hs-msg-head'>
                            <h2>{t('msg_title')}</h2>
                            <button className='hs-msg-x' onClick={onClose} aria-label={t('close')}>×</button>
                        </div>
                        <div className='hs-msg-listing'>
                            <div className='hs-msg-listing-photo' style={{ backgroundImage: `url(${listingCover(listing, api)})` }}></div>
                            <div className='hs-msg-listing-text'>
                                <span className='hs-msg-listing-title'>{listing.title}</span>
                                <span className='hs-msg-listing-meta'>{meta}</span>
                            </div>
                        </div>
                        <div className='hs-msg-to'>
                            <span>{t('msg_to')}</span>
                            <span className='hs-msg-to-chip'>{recipient}</span>
                            <span className='hs-msg-id'>{t('msg_listing_no', { id: listing.id })}</span>
                        </div>
                        <div className='hs-msg-fields'>
                            <label className='hs-msg-field'>
                                <span>{t('msg_subject')}</span>
                                <input value={subject} maxLength={500} placeholder={t('msg_subject_placeholder')}
                                    onChange={(e) => setSubject(e.target.value)} />
                            </label>
                            <label className='hs-msg-field'>
                                <span>{t('msg_body')}</span>
                                <textarea autoFocus rows={5} value={body} placeholder={t('msg_body_placeholder')}
                                    onChange={(e) => setBody(e.target.value)} />
                            </label>
                            <div className='hs-msg-templates'>
                                {TEMPLATES.map(key => (
                                    <button key={key} onClick={() => setBody(body ? `${body} ${t(key)}` : t(key))}>{t(key)}</button>
                                ))}
                            </div>
                            {error && <p className='hs-msg-error'>{t('msg_error')}</p>}
                        </div>
                        <div className='hs-msg-actions'>
                            <button className='hs-msg-cancel' onClick={onClose}>{t('cancel')}</button>
                            <button className='hs-msg-send' disabled={!canSend} onClick={send}>
                                {t('msg_send')}
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z" /></svg>
                            </button>
                        </div>
                    </>}
            </div>
        </div>
    );
};
